import {customElement, state} from "lit/decorators.js";
import {UnLitElement} from "../../../common/elements";
import {html, type TemplateResult} from "lit";
import {autoRegister, resolve} from "../../../container";
import {Dictionary} from "../dictionary";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingTextlinkInteract} from "../../../tracking/trackingAttributes";
import {LanguagesService} from "../../../common/languages";
import {GLOBAL} from "../../../common/globals";
import {AudibleTextCollector} from "./audibleTextCollector";


enum ReadingState {
    NONE, READING, PAUSED
}

type VoiceNamePattern = string[];

const PREFERRED_VOICES: { [lang: string]: Nullable<VoiceNamePattern[]> } = {
    "de": [
        ["Google Deutsch"],
        ["Microsoft Katja Online", "Natural", "German"],
        ["Microsoft Conrad Online", "Natural", "German"],
        ["Microsoft Stefan", "German"],
        ["Anna"], // iPhone, really bad
        ["Deutsch Deutschland"] // Android Chrome, really bad
    ],
    "en": [
        ["Microsoft Jenny Online", "Natural", "English", "United States"],
        ["Microsoft Michelle Online", "Natural", "English", "United States"],
        ["Microsoft Andrew Online", "Natural", "English", "United States"],
        ["Google US English"],
        ["Microsoft Mark", "English", "United States"],
        ["Microsoft Zira", "English", "United States"],
        ["Englisch Vereinigte Staaten"] // Android Chrome, really bad
    ]
};

@autoRegister()
export class UtteranceFactory {
    public create(text: string): SpeechSynthesisUtterance {
        return new SpeechSynthesisUtterance(text);
    }
}

@customElement("eop-read-aloud-button")
export class EopReadAloudButton extends UnLitElement {

    private dictionary: Dictionary;
    private langId: string;
    private speech: SpeechSynthesis;
    private activeVoice: Nullable<SpeechSynthesisVoice> = null;

    @state()
    private state: ReadingState = ReadingState.NONE;

    public constructor(
        private textCollector: AudibleTextCollector = resolve(AudibleTextCollector),
        private utteranceFactory: UtteranceFactory = resolve(UtteranceFactory),
        languagesService: LanguagesService = resolve(LanguagesService)
    ) {
        super();
        this.langId = languagesService.activeLanguageId();
        this.speech = GLOBAL.browserWideSpeechSynthesis();
        this.speech.addEventListener("voiceschanged", () => this.activeVoice = this.determineActiveVoice());
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.state === ReadingState.READING) {
            this.speech.cancel();
        }
    }

    public render(): TemplateResult {
        if (this.state === ReadingState.READING) {
            return this.renderPauseButton();
        }
        if (this.state === ReadingState.PAUSED) {
            return this.renderResumeButton();
        }
        return this.renderStartButton();
    }

    private renderStartButton(): TemplateResult {
        return html`
            <button class="link read-aloud-button start-reading" @click=${this.start} ${trackingTextlinkInteract("read-aloud")}
            >${this.dictionary.translate("MSG_READ_ALOUD_BUTTON")}</button>
        `;
    }

    private renderPauseButton(): TemplateResult {
        return html`
            <button class="link read-aloud-button pause-reading" @click=${this.pause} ${trackingTextlinkInteract("pause-reading")}
            >${this.dictionary.translate("MSG_READ_ALOUD_PAUSE_BUTTON")}</button>
        `;
    }

    private renderResumeButton(): TemplateResult {
        return html`
            <button class="link read-aloud-button resume-reading" @click=${this.resume} ${trackingTextlinkInteract("resume-reading")}
            >${this.dictionary.translate("MSG_READ_ALOUD_RESUME_BUTTON")}</button>
        `;
    }

    private start(): void {
        this.activeVoice ??= this.determineActiveVoice();

        this.speech.cancel();
        const utterance = this.setupUtterance();
        this.speech.speak(utterance);
        this.state = ReadingState.READING;
    }

    private pause(): void {
        this.speech.pause();
        this.state = ReadingState.PAUSED;
    }

    private resume(): void {
        this.speech.resume();
        this.state = ReadingState.READING;
    }

    private setupUtterance(): SpeechSynthesisUtterance {
        const textBlocks = this.textCollector.determineAudibleTextBlocks();
        const text = textBlocks.join(". ");
        const utterance = this.utteranceFactory.create(text);
        utterance.voice = this.activeVoice;
        utterance.addEventListener("end", () => this.state = ReadingState.NONE);
        utterance.addEventListener("error", () => this.state = ReadingState.NONE); // e.g. when another utterance interrupts this one
        return utterance;
    }

    private determineActiveVoice(): Nullable<SpeechSynthesisVoice> {
        const available = this.speech.getVoices()
            .filter(v => v.lang.includes(this.langId));
        const preferredPatterns = PREFERRED_VOICES[this.langId];

        if (!preferredPatterns) {
            return null;
        }

        for (const pattern of preferredPatterns) {
            const match = available
                .findFirst(v => pattern.every(fragment => v.name.includes(fragment)));
            if (match) {
                return match;
            }
        }

        return null;
    }
}